'use client'

import { useMemo, useState } from 'react'
import { ChevronDown, ChevronUp, Search } from 'lucide-react'
import ImagePlaceholder from '@/components/ui/ImagePlaceholder'

type FAQ = { question: string; answer: string }
type FAQCategory = { name: string; faqs: FAQ[] }

// Updated FAQ content with user-specified copy and formatting
const faqCategoriesSource: FAQCategory[] = [
  {
    name: 'Leasing',
    faqs: [
      {
        question: 'What is a dry lease?',
        answer: 'The Lessee (person/entity leasing the aircraft) is responsible for all operational aspects such as fuel/oil, pilot, maintenance, labor, and insurance.',
      },
      {
        question: 'What are the lease costs/rates?',
        answer: 'All leases require a beginning deposit to secure an aircraft, and a regular monthly payment based on hours flown at an hourly rate. Most aircraft have a minimum usage requirement.\n\nLessees are responsible for all maintenance and labor costs, as well as fuel/oil, insurance, and parts and maintenance required to comply with Service Bulletins (SBs).\n\nAt lease end, the Lessee is responsible for a Return Annual Inspection.\n\nSpitzer covers the cost of parts needed for Airworthiness Directives (ADs), and all costs related to Overhauls.',
      },
      { 
        question: 'How long is the lease?', 
        answer: 'Typically a minimum of 1 year, but we also offer a variety of customizable options.' 
      },
      { 
        question: 'Can I upgrade or change aircraft during my lease?', 
        answer: 'Yes, we offer flexible upgrade options during your lease term. We can work with you to transition to different aircraft as your needs evolve.' 
      },
      {
        question: 'What are the pickup/return logistics?',
        answer: 'The Lessee is responsible for picking up the aircraft at one of our locations and returning it at the end of the lease.\n\nFlight time for pick up and return is billed at the hourly rate.',
      },
    ],
  },
  {
    name: 'Insurance',
    faqs: [
      {
        question: 'How does insurance work?',
        answer: 'The aircraft must be insured at a specified hull value prior to the Lessee taking possession.\n\nLessee is responsible for paying the deductible to Spitzer in the event of an insurance claim.\n\nIf damage is not covered by the Lessee\'s insurance, it must be repaired by Lessee to its original condition.',
      },
      { 
        question: 'What insurance requirements do you have?', 
        answer: 'We require comprehensive hull and liability insurance coverage. Minimum liability limits are typically $1,000,000 per occurrence. We can help arrange insurance through our network of aviation insurance providers.' 
      },
    ],
  },
]

export default function QASection({ id = 'faq', showSearch = true }: { id?: string; showSearch?: boolean }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [openFaqs, setOpenFaqs] = useState<Record<string, boolean>>({})

  const toggleFaq = (categoryIndex: number, faqIndex: number) => {
    const key = `${categoryIndex}-${faqIndex}`
    setOpenFaqs((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const filteredCategories = useMemo(() => {
    if (!searchTerm) return faqCategoriesSource
    return faqCategoriesSource
      .map((category) => ({
        ...category,
        faqs: category.faqs.filter(
          (faq) =>
            faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
            faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
        ),
      }))
      .filter((c) => c.faqs.length > 0)
  }, [searchTerm])

  const faqLd = useMemo(() => {
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqCategoriesSource.flatMap((c) => c.faqs).map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    }
  }, [])

  return (
    <section id={id}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />
      {showSearch && (
        <div className="mx-auto max-w-3xl px-6 lg:px-8 mb-6 sm:mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-lg border border-gray-300 focus:border-blue-500 focus:outline-none focus:ring-blue-500 text-lg text-gray-900 placeholder-gray-500"
            />
          </div>
        </div>
      )}

      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {filteredCategories.map((category, categoryIndex) => (
          <div key={category.name} className="mb-10 sm:mb-12">
            <div className="mb-8">
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center">{category.name}</h3>
            </div>
            <div className="space-y-4">
              {category.faqs.map((faq, faqIndex) => {
                const key = `${categoryIndex}-${faqIndex}`
                const isOpen = openFaqs[key]
                return (
                  <div key={faqIndex} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                    <button
                      onClick={() => toggleFaq(categoryIndex, faqIndex)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <h4 className="text-base sm:text-lg font-semibold text-gray-900 pr-4">{faq.question}</h4>
                      {isOpen ? (
                        <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                      )}
                    </button>
                    {isOpen && (
                      <div className="px-6 pb-4">
                        <div className="prose prose-gray max-w-none">
                          <div className="text-gray-600 leading-relaxed whitespace-pre-line">{faq.answer}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ))}

        {/* Removed trailing contact message per request */}
      </div>
    </section>
  )
}


